import React from "react";
import { Link } from "react-router-dom";
import { Overline, Section, PrimaryButton, OutlineButton } from "../components/UI";
import { SEO } from "../components/SEO";
import { ArrowUpRight, MessageCircle, Stethoscope, GraduationCap, CircuitBoard, BookOpen } from "lucide-react";
import { APPROVED_CONDITIONS } from "../data/approvedConditions";
import { APPROVED_CLINICAL_INSIGHTS } from "../data/approvedClinicalInsights";
import { APPROVED_ARTICLES } from "../data/approvedArticles";
import { CONTACT } from "../data/contactDetails";

// Homepage Article showcase reads directly from the frozen approved
// Articles source. Selections: Article 1, Article 4, Article 10
// (indices 0, 3, 9). This does not alter the /articles gallery of 10.
const HOMEPAGE_ARTICLES = [
  APPROVED_ARTICLES[0],
  APPROVED_ARTICLES[3],
  APPROVED_ARTICLES[9],
];

// Homepage credentials — inline layout (does not use shared TrustRibbon),
// so no shared component is modified. Full RCOG wording is retained at
// every screen width; wrapping is preferred over truncation.
const PRIMARY_CREDENTIALS = [
  { Icon: Stethoscope, label: "Senior High-Risk Pregnancy Consultant" },
  { Icon: GraduationCap, label: "MBBS · MS (Obstetrics & Gynaecology) · FIAOG" },
  { Icon: BookOpen, label: "International Medical Author" },
  { Icon: GraduationCap, label: "Associate Member, Royal College of Obstetricians and Gynaecologists,\u00A0London" },
  { Icon: CircuitBoard, label: "Artificial Intelligence in Healthcare (IIT Delhi; IIIT Hyderabad; ICTRD Nagpur)" },
];

// Homepage overline — slightly larger (~11.5px) and tighter tracking than
// the global Overline component. Scoped to homepage only so frozen pages
// remain unaffected.
const HomeOverline = ({ children, className = "" }) => (
  <div className={`mono text-[11px] md:text-[11.5px] uppercase tracking-[0.16em] text-prestige ${className}`}>
    {children}
  </div>
);

// Homepage CTA label (used for card "Read..." labels). Same restraint,
// slightly stronger legibility.
const CardCta = ({ children }) => (
  <div className="mt-6 flex items-center gap-1.5 text-navy text-[11px] md:text-[11.5px] mono uppercase tracking-[0.16em] opacity-75 group-hover:opacity-100 transition-opacity">
    {children} <ArrowUpRight size={14} strokeWidth={1.5} />
  </div>
);

export default function Home() {
  return (
    <div>
      <SEO/>
      {/* Hero — preserved verbatim */}
      <section className="relative overflow-hidden bg-bone" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid md:grid-cols-2 gap-10 md:gap-6 items-center">
          <div className="order-2 md:order-1">
            <Overline>Dr. Tania Gurdip Singh · High-Risk Pregnancy Consultant</Overline>
            <div className="h-px bg-prestige my-6 origin-left animate-[goldrule_0.6s_ease-out_forwards]" style={{width: 48}} />
            <h1
              className="font-serif italic text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-prestige tracking-tight animate-fade-up"
              data-testid="hero-philosophy"
            >
              Preventing complications<br />before they begin.
            </h1>
            <p style={{animationDelay:'0.3s'}} className="text-lg text-stone2 mt-6 max-w-xl leading-relaxed animate-fade-up">
              A dedicated platform for high-risk pregnancy and recurrent pregnancy loss — grounded in evidence, guided by international guidelines, and shaped by two decades of clinical experience.
            </p>
            <div style={{animationDelay:'0.5s'}} className="flex flex-wrap gap-3 mt-8 animate-fade-up">
              <PrimaryButton to="/contact?type=appointment" testid="hero-book-cta" className="!bg-prestige !text-navy hover:!bg-prestige-soft focus-visible:ring-prestige">Book a Consultation</PrimaryButton>
              <OutlineButton to="/conditions" testid="hero-explore-cta">Explore Conditions</OutlineButton>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <picture>
              <source
                type="image/webp"
                srcSet="/brand/dr-tania-hero-640.webp 640w, /brand/dr-tania-hero-1000.webp 1000w, /brand/dr-tania-hero-1600.webp 1600w"
                sizes="(min-width: 1280px) 620px, (min-width: 768px) 48vw, 100vw"
              />
              <img
                src="/brand/dr-tania-hero-1000.jpg"
                srcSet="/brand/dr-tania-hero-640.jpg 640w, /brand/dr-tania-hero-1000.jpg 1000w, /brand/dr-tania-hero-1600.jpg 1600w"
                sizes="(min-width: 1280px) 620px, (min-width: 768px) 48vw, 100vw"
                alt="Dr. Tania Gurdip Singh at her consulting desk with medical reference books"
                className="w-full aspect-[5/4] object-cover"
                style={{ objectPosition: "50% 40%" }}
                loading="eager"
                fetchPriority="high"
                width="1600" height="1280"
                data-testid="hero-image"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* ================================================================
          FROZEN — Homepage Credential Section
          (final composition approved 3 August 2026)
          ---------------------------------------------------------------
          Row 1 (lg: col-span-4 × 3):
            1. Senior High-Risk Pregnancy Consultant
            2. MBBS · MS (Obstetrics & Gynaecology) · FIAOG
            3. International Medical Author
          Row 2 (lg: col-span-6 × 2):
            4. Associate Member, Royal College of Obstetricians and
               Gynaecologists, London   (no "(RCOG)" abbreviation)
            5. Artificial Intelligence in Healthcare
               (IIT Delhi; IIIT Hyderabad; ICTRD Nagpur)

          Grid: single shared lg:grid-cols-12, 4-4-4 / 6-6.
          Tablet: sm:grid-cols-2 · Mobile: 1-per-row stack.
          Icons: Stethoscope, GraduationCap, BookOpen, GraduationCap,
          CircuitBoard (from lucide-react). Padding: pt-11 md:pt-12 /
          pb-10 md:pb-11 · gap-y-7 md:gap-y-8. Philosophy section top
          spacing: pt-12 md:pt-16 (~64 px divider-to-eyebrow gap).

          The AI credential is rendered as one semantic text unit.
          Individual institution names (IIT Delhi, IIIT Hyderabad,
          ICTRD Nagpur) are wrapped in whitespace-nowrap spans so no
          single institution can split across lines. At widths where
          the credential fits naturally on one physical line, it MUST
          stay on one line. Natural wrapping only — do NOT force a
          two-line layout at any desktop/laptop width.

          The RCOG credential uses a non-breaking space between
          "Gynaecologists," and "London" so, when the credential ever
          wraps at narrower widths, "London" cannot orphan onto a new
          line.

          Do NOT during any future task:
            • Restore the "(RCOG)" abbreviation.
            • Force the AI credential onto two lines when width allows one.
            • Shrink the font on any credential.
            • Treat any credential line as a subtitle / secondary text.
            • Reintroduce "Additional Professional Training" here
              (those two qualifications live in
              /app/frontend/src/data/additionalTraining.js and are
              reserved for the future About Dr Tania page only).
            • Split the AI institutions into a subtitle.
            • Add cards / logos / crests / dates / certificates /
              shadows / animations to any credential.
            • Modify the wording, order, icons, spacing or grid.
          ================================================================ */}
      {/* Credentials — 5 primary in single 12-col grid (4+4+4 / 7+5) */}
      <section
        aria-label="Credentials"
        data-testid="trust-ribbon"
        data-frozen="homepage-credentials-approved-2026-08-03"
        className="bg-prestige-bg border-y border-prestige/40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 lg:px-12 pt-11 md:pt-12 pb-10 md:pb-11">
          {/* Single 12-col grid: row 1 = 4+4+4, row 2 = 7+5. Full-width on both rows. */}
          <ul
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-x-8 md:gap-x-10 gap-y-7 md:gap-y-8 items-start"
            role="list"
            data-testid="primary-credentials"
          >
            {PRIMARY_CREDENTIALS.map((c, i) => {
              // Desktop layout on lg+ (single shared 12-col grid):
              //   Row 1 — items 0/1/2 each span 4 columns (1-4, 5-8, 9-12)
              //   Row 2 — items 3 (RCOG) + 4 (AI) each span 6 columns (1-6, 7-12)
              const lg = i < 3 ? "lg:col-span-4" : "lg:col-span-6";
              const { Icon, label, sub } = c;
              const slug = label
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
              return (
                <li
                  key={label}
                  data-testid={`credential-${slug}`}
                  className={`flex items-start gap-3 min-w-0 w-full ${lg}`}
                >
                  <Icon size={18} strokeWidth={1.5} className="text-prestige shrink-0 mt-[3px]" aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="text-[13.5px] md:text-[14px] leading-snug text-navy font-medium tracking-[0.005em] break-words">
                      {label.startsWith("Artificial Intelligence") ? (
                        // AI credential rendered as one semantic text unit.
                        // Natural wrapping only. Individual institution
                        // names wrapped in whitespace-nowrap spans so no
                        // single institution can split across lines.
                        <>
                          Artificial Intelligence in Healthcare (
                          <span className="whitespace-nowrap">IIT Delhi</span>
                          ;{" "}
                          <span className="whitespace-nowrap">IIIT Hyderabad</span>
                          ;{" "}
                          <span className="whitespace-nowrap">ICTRD Nagpur</span>
                          )
                        </>
                      ) : (
                        label
                      )}
                    </div>
                    {sub && (
                      <div className="text-[11.5px] leading-snug text-stone2 font-normal tracking-[0.01em] mt-1 break-words">
                        {sub}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Philosophy — heading preserved, body paragraphs replaced */}
      <section className="pt-12 md:pt-16 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-5">
            <HomeOverline>Philosophy</HomeOverline>
            <div className="h-px w-10 bg-prestige my-5"/>
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              A different approach to high-risk pregnancy.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-6">
            <p className="text-lg text-stone2 leading-relaxed">
              In high-risk pregnancy care, some of the most important decisions are made before complications become visible—during preconception planning, in early pregnancy and in the way the complete risk profile is assembled.
            </p>
            <p className="text-lg text-stone2 leading-relaxed mt-5">
              This platform is built around that approach. Every condition guide, Article and Clinical Insight is designed to help patients and clinicians prepare, anticipate and—where possible—prevent.
            </p>
          </div>
        </div>
      </section>

      {/* Specialist Conditions — all 8 approved, no Explore-all link */}
      <section className="py-14 md:py-20 bg-surfacealt/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <HomeOverline>Specialist Conditions</HomeOverline>
            <div className="h-px w-10 bg-prestige my-4" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              Complex pregnancies need condition-specific care.
            </h2>
            <p className="text-base md:text-lg text-stone2 mt-6 leading-relaxed">
              Explore specialist guidance on complex pregnancy conditions where diagnosis, surveillance and timing matter.
            </p>
          </div>
          <div
            className="mt-9 md:mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6"
            data-testid="home-conditions-grid"
          >
            {APPROVED_CONDITIONS.slice(0, 8).map((c) => (
              <Link
                key={c.slug}
                to={c.route}
                data-testid={`home-condition-card-${c.slug}`}
                className="group flex flex-col justify-between h-full bg-white border border-line p-6 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
              >
                <div>
                  <HomeOverline>{c.category}</HomeOverline>
                  <h3 className="font-serif text-xl text-navy mt-3 leading-snug group-hover:underline underline-offset-4">
                    {c.title}
                  </h3>
                </div>
                <CardCta>Read specialist guide</CardCta>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Dr Tania's Clinical Insights — 2×2 editorial grid on desktop */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <HomeOverline>Dr Tania’s Clinical Insights</HomeOverline>
            <div className="h-px w-10 bg-prestige my-4" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              The detail that can change the decision.
            </h2>
            <p className="text-base md:text-lg text-stone2 mt-6 leading-relaxed">
              Short specialist interpretations of findings that are often oversimplified, mislabelled or treated without considering the complete clinical picture.
            </p>
          </div>
          <div
            className="mt-9 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7"
            data-testid="home-insights-grid"
          >
            {APPROVED_CLINICAL_INSIGHTS.slice(0, 4).map((i) => (
              <Link
                key={i.slug}
                to={`/clinical-insights/${i.slug}`}
                data-testid={`home-insight-card-${i.slug}`}
                className="group flex flex-col justify-between h-full bg-bone border-l-2 border-prestige border-y border-r border-y-line border-r-line p-7 md:p-8 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
              >
                <div>
                  <div className="mono text-[11px] uppercase tracking-[0.18em] text-prestige">
                    {i.variant}
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl text-navy mt-3 leading-snug group-hover:underline underline-offset-4">
                    {i.title}
                  </h3>
                </div>
                <CardCta>Read Clinical Insight</CardCta>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Articles — 3 aligned cards */}
      <section className="py-14 md:py-20 bg-surfacealt/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <HomeOverline>Expert Articles</HomeOverline>
            <div className="h-px w-10 bg-prestige my-4" />
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight">
              Focused answers to difficult pregnancy questions.
            </h2>
            <p className="text-base md:text-lg text-stone2 mt-6 leading-relaxed">
              Clear, specialist explanations of medical and surgical disorders in pregnancy—what the finding means, what changes during pregnancy and when closer assessment may be needed.
            </p>
          </div>
          <div
            className="mt-9 md:mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7"
            data-testid="home-articles-grid"
          >
            {HOMEPAGE_ARTICLES.map((a) => (
              <Link
                key={a.slug}
                to={`/articles/${a.slug}`}
                data-testid={`home-article-card-${a.slug}`}
                className="group flex flex-col justify-between h-full bg-white border border-line p-7 hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2"
              >
                <div>
                  <HomeOverline>{a.category}</HomeOverline>
                  <h3 className="font-serif text-xl md:text-2xl text-navy mt-3 leading-snug group-hover:underline underline-offset-4">
                    {a.title}
                  </h3>
                  <p className="text-sm text-stone2 mt-4 leading-relaxed">
                    {a.cardDescription}
                  </p>
                </div>
                <CardCta>Read Article</CardCta>
              </Link>
            ))}
          </div>
          <div className="mt-8 md:mt-10">
            <Link
              to="/articles"
              data-testid="home-articles-explore-all"
              className="mono text-[11px] md:text-[11.5px] uppercase tracking-[0.16em] text-navy inline-flex items-center gap-1.5 hover:text-prestige transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm"
            >
              Explore all 10 Articles <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-14 md:py-20 bg-navy-deep text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <HomeOverline className="!text-prestige-soft">Consultation</HomeOverline>
            <h2 className="font-serif text-3xl md:text-5xl text-white mt-4 tracking-tight leading-tight">Considering a consultation with Dr. Tania?</h2>
            <p className="text-white/70 mt-4 max-w-xl">Share your concern or referral request. Our team will guide you to the most appropriate consultation option.</p>
          </div>
          <div className="md:col-span-4 flex flex-wrap gap-3">
            <Link to="/contact?type=appointment" data-testid="closing-book" className="bg-prestige hover:bg-prestige-soft text-navy rounded-full px-6 py-3 text-sm font-medium">Book a Consultation</Link>
            <a href={CONTACT.primary.whatsapp} data-testid="closing-whatsapp" aria-label={`Message on WhatsApp: ${CONTACT.primary.display}`} className="border border-white/40 text-white rounded-full px-6 py-3 text-sm font-medium flex items-center gap-2"><MessageCircle size={14} strokeWidth={1.5}/> WhatsApp</a>
          </div>
        </div>
      </section>
    </div>
  );
}
